import type { FunctionalComponent } from 'vue'
import { message } from 'ant-design-vue'
import { TemplateShare } from './templateShare'
// import { Tags } from './tags'
import TagList from './tag-list.vue'
import type { Tag, TagIds, Template } from '~/api/interface'
import Collect from '@/features/collect/index.vue'
import { duplicateOpus } from '~/api'
import Resource from '~/components/resource'
import { IAfterLoginActionType } from '~/utils/loginTask'
import { useTime } from '~/hooks/useTime'
import type { TagInfo } from '~/composables/tags'
import { PageKey } from '~/constants/page'

export const useTemplate = async (
  data?: Template | null,
  linkQuery = '' // 跳转所需的参数
) => {
  const { $t } = useNuxtApp()
  const resourceLocation = injectResourceLocation()
  const route = useRoute()

  if (data?.online_status === 1 && data?.is_template_center_template === 1) {
    return message.error($t('gai-zuo-pin-mo-ban-yi-xia-jia-jin-gong-zhan-shi'))
  }

  function enhanceJumpLink(link: string) {
    if (linkQuery) {
      return `${link}?${linkQuery}`
    }

    return link
  }

  if (!data) {
    return
  }

  trackEvent(TRACK_EVENT.CLICK_TEMPLATE_USE, {
    template_id: data?.model_id || data?.id,
    template_title: data.title,
    tags: getTagIDs(data.tag_ids).map((item) => item.tag_name),
    source: resourceLocation?.source,
  })

  if (data.copyright_limited) {
    trackEvent(TRACK_EVENT.启动编辑器, {
      page: route.name,
      source: resourceLocation?.source,
    })
    window.location.href = enhanceJumpLink(
      `/editor/${data.id}/${data.secret_id}`
    )
    return
  }

  const action = {
    type: IAfterLoginActionType.USE_TEMPLATE,
    payload: [data, linkQuery],
  }

  try {
    loginTask.add(action)
    // 团队空间是model_id  其他是id
    const res = await duplicateOpus(data?.model_id || data?.id)
    loginTask.remove(action)
    trackEvent(TRACK_EVENT.启动编辑器, {
      source: resourceLocation?.source,
      page: route.name,
    })

    window.location.href = enhanceJumpLink(
      `/editor/${res?.data.id}/${res?.data.secret_id}`
    )
  } catch (error) {
    console.error(error)
  }
}

export const enhanceTagByName = (name: string) => {
  const TagMap = toValue(useTagMap())
  const obj: Partial<Tag> = { tag_name: name }
  const match = Object.values(TagMap).find((item) => item.tag_name === name)
  if (match) {
    Object.assign(obj, match)
  }
  return obj
}

export const getTagIDs = (ids?: TagIds) => {
  if (!ids || JSON.stringify(ids) === '{}') {
    return [] as TagInfo[]
  }

  const TagMap = toValue(useTagMap())
  const TagKeys = Object.keys(TagMap)
  const result: TagInfo[] = []
  const fn = (ids: TagIds, level = 0, pid = '') => {
    for (const key in ids) {
      const value = ids[key]
      const parentId = `${pid ? pid + ':' : ''}${level},${key}`
      if (level > 0 && TagMap[`${parentId}`]) {
        result.push(TagMap[`${parentId}`])
      }

      if (Array.isArray(value)) {
        value.forEach((v) => {
          const id = `${parentId}:${level + 1},${v}`
          const key = TagKeys.find((k) => k.endsWith(`${v}`))
          if (TagMap[id] || key) {
            result.push(TagMap[id] || TagMap[key as string])
          }
        })
      } else if (typeof value === 'object') {
        fn(value, level + 1, parentId)
      }
    }
  }
  fn(ids)
  return result
}

export const TemplateInfo: FunctionalComponent<
  {
    data: Template
    onChange: (params: any) => void
    templateSource?: TEMPLATE_SOURCE
  },
  { change: (params: any) => void }
> = (props, { emit }) => {
  const { $t } = useNuxtApp()
  const { timeFormat } = useTime()
  const { message } = props.data
  const userInfo = useUserInfo()
  const resourceLocation = injectResourceLocation()

  function changeUrl() {
    if (!props.data) {
      return
    }
    const memoizedPath = useMemoizedPath()
    if (memoizedPath.value === null) {
      memoizedPath.value = new URL(window.location.href).pathname
    }
    const link = `/template/${props.data.id}.html`
    history.replaceState({}, '', link)
  }

  watch(() => props.data, changeUrl, {
    immediate: true,
  })

  const nameJumpable = computed(() => {
    return true
  })

  const handClick = () => {
    if (!nameJumpable.value) {
      return
    }

    if (props.data) {
      const router = useRouter()
      router.push({
        name: PageKey.PERSONAL_PAGE,
        params: {
          key: props.data.homepage_org_id || 0,
        },
      })
      const open = useTemplateVisible()
      open.value = false
    }
  }
  const getClass = () => {
    return 'flex-y-center gap-[6px] cursor-pointer hover:text-font-60'
  }

  const isBestWorker =
    props.templateSource === TEMPLATE_SOURCE.BEST_PRACTICE_PAGE

  const isOnline =
    props?.data?.online_status === 0 || props.data.online_status === undefined
  // 未上架不可操作, 0 表示上架, undefined 表示没这个字段默认当做上架了

  const descriptionDisplay = (() => {
    if (!message) {
      return {
        isOutOfRange: false,
        title: () => <div></div>,
        default: () => <div></div>,
      }
    }
    const tmp = document.createElement('div')
    tmp.innerHTML = message
    const isTextNode = tmp.childNodes[0].nodeType === Node.TEXT_NODE
    let isOutOfRange = false
    if (isTextNode) {
      isOutOfRange = tmp.innerText.length > 60
    } else {
      isOutOfRange = tmp.childNodes.length > 1
    }
    return {
      isOutOfRange,
      title: () => <div v-html={tmp.innerHTML}></div>,
      default: () => {
        if (isTextNode) {
          return tmp.innerText.slice(0, 60) + (isOutOfRange ? '...' : '')
        } else {
          const copy = document.createElement('div')
          tmp.childNodes.forEach((item, idx) => {
            if (idx <= 1) {
              copy.appendChild(item.cloneNode(true))
            }
          })
          return (
            <div v-html={copy.innerHTML + (isOutOfRange ? '...' : '')}></div>
          )
        }
      },
    }
  })()

  return (
    <div class="flex flex-col justify-between flex-1">
      <div>
        {isBestWorker && (
          <div
            class="worker-wrapper p-[6px_12px] mb-[20px] mt-[4px]"
            style={{
              background: `url(${transformImage(
                getFeUrl('/youyan-platform/png/best-worker-bg.png')
              )}) no-repeat center center/cover`,
            }}
          >
            <span onClick={handClick} class={[getClass(), '!gap-[12px]']}>
              <Resource
                class="w-[36px] h-[36px] rounded-[50%]"
                style="border: 1px solid var(--purple-5);"
                src={props.data?.avatar_url || props.data?.user_logo_url}
                transformResource={(url: string) =>
                  transformImage(url, { height: 36 })
                }
              />
              <div class="text-14 font-semibold truncate max-w-[280px]">
                {props.data?.name_for_show || props.data?.username}
              </div>
            </span>
          </div>
        )}
        <div class="text-16 font-semibold text-font-1 flex-y-center truncate">
          {props.data?.tags?.includes($t('re-men')) && !props.data?.is_new && (
            <svg
              viewBox="0 0 14 14"
              class="h-[20px] w-[20px] mr-[5px] "
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                data-follow-fill="#FFFFFF"
                fill-rule="evenodd"
                fill="#FF5656"
                d="M7 14c2.882 0 5.25-2.284 5.25-5.216 0-.72-.037-1.49-.436-2.69-.399-1.199-.479-1.354-.9-2.094-.18 1.51-1.145 2.14-1.39 2.329 0-.196-.583-2.361-1.468-3.657C7.188 1.4 6.007.566 5.315 0c0 1.074-.302 2.672-.735 3.486-.433.814-.514.843-1.055 1.45-.54.605-.788.792-1.24 1.527-.452.735-.535 1.714-.535 2.433C1.75 11.828 4.118 14 7 14Z"
              />
            </svg>
          )}
          {props.data?.is_new && (
            <iconpark-icon
              name="new-standard"
              size="40"
              class="mr-2"
            ></iconpark-icon>
          )}
          {props.data?.name}
        </div>
        {message && descriptionDisplay.isOutOfRange ? (
          <a-tooltip
            destroyTooltipOnHide={true}
            placement="bottom"
            overlayStyle={{ maxWidth: '340px', paddingTop: 0 }}
            overlayInnerStyle={{ color: 'rgba(18, 25, 38, 0.6)' }}
            color="white"
          >
            {{
              title: descriptionDisplay.title,
              default: descriptionDisplay.default,
            }}
          </a-tooltip>
        ) : (
          <div class="text-12 text-font-40 mt-[8px]" v-html={message}></div>
        )}
        {!isBestWorker && (
          <div class="text-12 text-font-40 mt-[24px] flex-y-center">
            <span onClick={handClick} class={getClass()}>
              <Resource
                class="w-[16px] h-[16px] rounded-[50%]"
                style="border: 1px solid var(--back-3);"
                src={props.data?.avatar_url || props.data?.user_logo_url}
                transformResource={(url: string) =>
                  transformImage(url, { height: 36 })
                }
              />
              {props.data?.name_for_show || props.data?.username}
            </span>
            <a-divider type="vertical" />
            <span>
              {$t('fa-bu-yu')}{' '}
              {timeFormat(props.data?.online_time || props.data?.create_time)}
            </span>
          </div>
        )}
      </div>
      <div>
        <TagList new_tag_ids={props.data?.new_tag_ids} />
        <a-divider style="margin: 15px 0" />
        <div class="flex-between gap-[20px]">
          {isOnline ? (
            <a-button
              type="primary"
              class="h-[28px] flex-1"
              onClick={() => {
                // if (!props.data?.copyright_limited) {
                useTemplate(props.data, props?.templateSource)
                // }
              }}
            >
              {props.data?.copyright_limited
                ? $t('ban-quan-xiang-guan-zhi-du-mo-ban')
                : userInfo.value
                ? $t('chuang-zuo-tong-kuan')
                : $t('mian-fei-zhu-ce-chuang-zuo-tong-kuan')}
            </a-button>
          ) : (
            <a-popover
              content={$t('shang-jia-hou-ji-ke-cao-zuo')}
              class="flex-1"
            >
              <a-button type="primary" class="h-[28px] w-full" disabled={true}>
                {props.data?.copyright_limited
                  ? $t('ban-quan-xiang-guan-zhi-du-mo-ban')
                  : userInfo.value
                  ? $t('chuang-zuo-tong-kuan')
                  : $t('mian-fei-zhu-ce-chuang-zuo-tong-kuan')}
              </a-button>
            </a-popover>
          )}
          <div class="flex-center gap-[12px]">
            <Collect
              class={[
                '!w-[28px] !h-[28px] !text-[20px] cursor-pointer !shadow-none',
                `${!isOnline ? '!cursor-not-allowed opacity-60' : ''}`,
              ]}
              data={props.data}
              modelValue={props.data?.is_star_personal}
              params={{
                model_id: props.data?.id || -1,
                model_type: COLLECTION_MAP.PIECES_TEMPLATE,
                source: COLLECTION_SOURCE.YOUYAN,
              }}
              disabled={!isOnline}
              onChange={(v) => emit('change', ['is_star_personal', v])}
            />
            <TemplateShare
              templateId={props.data?.id}
              trackParams={{
                target: TARGET_FROM.Template,
                dataId: props.data.id,
                dataTitle: props.data.name,
                tags: getTagIDs(props.data.tag_ids).map(
                  (item) => item.tag_name
                ),
                source: resourceLocation?.source,
              }}
              params={{
                share_ids: [props.data?.id],
                model_type: COLLECTION_MAP.PIECES_TEMPLATE,
              }}
              disabled={!isOnline}
              linkUrl={`${location?.origin}/template/${props.data?.id}.html`}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
